/* global process */
import { createClient, type Client } from "@osdk/client";
import { getTokens, isExpired } from "./tokenStore";
import { signIn, type ProgressFn, type RequestUrlFn } from "./signIn";

let cachedClient: Client | undefined;
let currentProgress: ProgressFn = () => {};
let currentRequestUrl: RequestUrlFn = () => Promise.reject(new Error("No URL prompt registered"));

export function setAuthProgress(fn: ProgressFn): void {
  currentProgress = fn;
}

export function setAuthRequestUrl(fn: RequestUrlFn): void {
  currentRequestUrl = fn;
}

export function resetClient(): void {
  cachedClient = undefined;
}

// Run signIn directly if we don't have a valid cached token. Callers should
// invoke this BEFORE making any OSDK request — otherwise the OSDK ends up
// awaiting our auth callback through the entire user-interactive OAuth flow,
// and in WKWebView the subsequent fetch silently hangs.
export async function ensureSignedIn(): Promise<void> {
  const tokens = await getTokens();
  if (tokens && !isExpired(tokens)) return;
  await signIn(currentRequestUrl, currentProgress);
}

export function getClient(): Client {
  if (cachedClient) return cachedClient;

  const osdkUrl = process.env.OSDK_URL!;
  const ontologyRid = process.env.OSDK_ONTOLOGY_RID!;

  // Auth callback used by the OSDK. With ensureSignedIn() called first,
  // this normally returns immediately from storage. It's still a fallback
  // for mid-session token expiry.
  cachedClient = createClient(osdkUrl, ontologyRid, async () => {
    const tokens = await getTokens();
    if (tokens && !isExpired(tokens)) return tokens.accessToken;
    const fresh = await signIn(currentRequestUrl, currentProgress);
    return fresh.accessToken;
  });

  return cachedClient;
}
